import { useToast } from '@lawpilots/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CertificateDto } from '@/services/api/tenant/certificates'
import { certificateFileName, fetchCertificatePdf } from '../utils/certificatePdf'

/** The download needs the link and the date its filename carries, and nothing else. */
type DownloadableCertificate = Pick<CertificateDto, 'obtained_on' | 'url'>

/**
 * Downloads one certificate, shared by the card action and the preview modal. Fetching rather than
 * pointing an anchor at the presigned url is what lets a failed request surface as a toast instead
 * of a blank tab.
 */
export const useCertificateDownload = (
  userFullName: string,
): ((certificate: DownloadableCertificate) => Promise<void>) => {
  const { t } = useTranslation('general')
  const toast = useToast()

  return useCallback(
    async (certificate: DownloadableCertificate) => {
      if (!certificate.url) {
        toast.error(t('cert_download_error'))
        return
      }

      try {
        const blob = await fetchCertificatePdf(certificate.url)
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.setAttribute('download', certificateFileName(userFullName, certificate.obtained_on))
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)
        toast.success(t('cert_download_success'))
      } catch {
        toast.error(t('cert_download_error'))
      }
    },
    [t, toast, userFullName],
  )
}
